import React from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';

interface TimeoutTimerProps {
    /** Timeout in seconds */
    timeout: number;
    /** Callback when timer expires */
    onExpire: () => void;
    /** Optional: pause the timer */
    isPaused?: boolean;
}

const TimeoutTimer = ({ timeout, onExpire, isPaused = false }: TimeoutTimerProps) => {
    const [remainingTime, setRemainingTime] = React.useState<number>(timeout);

    const intervalIdRef = React.useRef<number | null>(null);
    const hasExpiredRef = React.useRef<boolean>(false);
    const onExpireRef = React.useRef<() => void>(onExpire);

    React.useEffect(() => {
        onExpireRef.current = onExpire;
    }, [onExpire]);

    const clearTimer = React.useCallback(() => {
        if (intervalIdRef.current !== null) {
            window.clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    }, []);

    React.useEffect(() => {
        hasExpiredRef.current = false;
        setRemainingTime(timeout);
        clearTimer();
    }, [timeout, clearTimer]);

    const isExpired = remainingTime <= 0;

    React.useEffect(() => {
        if (!isExpired) return;
        if (hasExpiredRef.current) return;

        hasExpiredRef.current = true;
        clearTimer();
        onExpireRef.current();
    }, [isExpired, clearTimer]);

    React.useEffect(() => {
        if (isPaused) {
            clearTimer();
            return;
        }

        if (isExpired) {
            clearTimer();
            return;
        }

        if (intervalIdRef.current !== null) return;

        intervalIdRef.current = window.setInterval(() => {
            setRemainingTime((prevRemainingTime) => {
                if (prevRemainingTime <= 1) {
                    clearTimer();
                    return 0;
                }
                return prevRemainingTime - 1;
            });
        }, 1000);

        return () => clearTimer();
    }, [isPaused, isExpired, clearTimer]);

    const progress = timeout > 0 ? (remainingTime / timeout) * 100 : 0;

    const isWarning = remainingTime <= 30 && remainingTime > 10;
    const isCritical = remainingTime <= 10;

    const formatTime = React.useCallback((seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }, []);

    const getColor = React.useCallback(() => {
        if (isCritical) return 'error';
        if (isWarning) return 'warning';
        return 'primary';
    }, [isCritical, isWarning]);

    if (isExpired) {
        return (
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                Time expired. The secret has been hidden for security.
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: (theme) =>
                    isCritical
                        ? `${theme.palette.error.main}10`
                        : isWarning
                          ? `${theme.palette.warning.main}10`
                          : `${theme.palette.primary.main}10`,
                border: '1px solid',
                borderColor: isCritical ? 'error.main' : isWarning ? 'warning.main' : 'primary.main',
                transition: 'all 0.3s ease',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon
                        color={getColor()}
                        sx={{
                            fontSize: 20,
                            animation: isCritical ? 'pulse 1s infinite' : 'none',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />
                    <Typography variant="body2" color={`${getColor()}.main`} fontWeight={500}>
                        Time remaining to view
                    </Typography>
                </Box>

                <Typography
                    variant="h6"
                    color={`${getColor()}.main`}
                    fontWeight={700}
                    fontFamily="monospace"
                    sx={{
                        animation: isCritical ? 'pulse 1s infinite' : 'none',
                    }}
                >
                    {formatTime(remainingTime)}
                </Typography>
            </Box>

            <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, progress))}
                color={getColor()}
                sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: (theme) => theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        transition: 'transform 1s linear',
                    },
                }}
            />

            {isCritical && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                    ⚠️ Secret will be hidden soon! Copy important data now.
                </Typography>
            )}
        </Box>
    );
};

export default TimeoutTimer;
